import { LinkButton } from '@euler/app/components/LinkButton';
import { DigitalSignModal } from '@euler/app/components/signature/DigitalSignModal';
import { Typography } from '@euler/app/components/typography';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { MediaUploadTaskEvent } from '@euler/app/flows/task/functions/task-detail/MediaFileUploadController';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { Img } from '@euler/components/image/AdvancedImage';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { mimeTypeFromFileUri, showAlert } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { memo, useCallback, useState } from 'react';
import {
  ActivityIndicator,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { Fill } from 'react-slot-fill';
import { ServiceAgreementForSignatureModal } from './ServiceAgreementForSignatureModal';

export const WiredPreInspectionSignature = memo(
  ({ style }: { style?: StyleProp<ViewStyle> }) => {
    const { taskManager, detail } = useTaskContext();

    const [uploadEvent, setUploadEvent] = useState<MediaUploadTaskEvent>();
    const [localFileUri, setLocalFileUri] = useState<string>();
    const [isTosVisible, setIsTosVisible] = useState(false);
    const [isSignVisible, setIsSignVisible] = useState(false);

    const isUploading =
      uploadEvent?.type === 'begin' ||
      uploadEvent?.type === 'queued' ||
      uploadEvent?.type === 'progress';

    const uploadError =
      uploadEvent?.type === 'error' ? uploadEvent.error : undefined;

    const upload = useCallback(
      (uri: string) => {
        taskManager.uploadController.enqueue({
          localFileUri: uri,
          contentType: mimeTypeFromFileUri(uri, 'image/jpeg'),
          type: 'image',
          listener: async event => {
            // will not show progress here.
            if (event.type === 'progress') return;

            setUploadEvent(event);

            if (event.type === 'finished') {
              try {
                await taskManager.updateBasicInfo({
                  preInspectionSignatureImgUrl: event.result.url,
                });
              } catch (e) {
                void showAlert('出错了', (e as Error).message);
              }
            }
          },
        });
      },
      [taskManager],
    );

    const onSignConfirm = useCallback(
      (uri: string | undefined) => {
        if (!uri) return;
        setLocalFileUri(uri);
        upload(uri);
        setIsSignVisible(false);
      },
      [upload],
    );

    const onRetry = useCallback(() => {
      if (!localFileUri) return;
      upload(localFileUri);
    }, [localFileUri, upload]);

    const onSign = useCallback(() => {
      setIsTosVisible(true);
    }, []);

    const onTosDismiss = useCallback(() => {
      setIsTosVisible(false);
    }, []);

    const onResign = useCallback(() => {
      setIsSignVisible(true);
    }, []);

    const onTosConfirm = useCallback(() => {
      setIsTosVisible(false);
      setIsSignVisible(true);
    }, []);

    const onSignDismiss = usePersistFn(() => {
      setIsSignVisible(false);
    });

    return (
      <>
        <PreInspectionSignature
          signatureImageUrl={
            localFileUri ?? detail.preInspection.signatureImgUrl
          }
          style={style}
          uploading={isUploading}
          error={uploadError}
          onPress={onSign}
          onRetry={onRetry}
          onResign={onResign}
        />
        <Fill name="task:bottom-panel">
          <ServiceAgreementForSignatureModal
            isVisible={isTosVisible}
            onConfirm={onTosConfirm}
            onDismiss={onTosDismiss}
          />
          <DigitalSignModal
            visible={isSignVisible}
            onDismiss={onSignDismiss}
            onConfirm={onSignConfirm}
            onHide={onSignDismiss}
          />
        </Fill>
      </>
    );
  },
);

export const PreInspectionSignature = memo(
  ({
    style,
    signatureImageUrl,
    uploading,
    error,
    onPress,
    onRetry,
    onResign,
  }: {
    style?: StyleProp<ViewStyle>;
    signatureImageUrl?: string;
    uploading?: boolean;
    error?: Error;
    onPress?: () => void;
    onRetry?: () => void;
    onResign?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        css={`
          background-color: #fff;
        `}
        style={style}
      >
        <HStack
          css={`
            padding: ${rpx(10)}px ${rpx(15)}px;
            height: ${rpx(50)}px;
          `}
        >
          <HStack>
            <SignIcon />
            <Typography.Label
              regular
              size={rpx(19)}
              css={`
                margin-left: ${rpx(8)}px;
              `}
            >
              签名确认
            </Typography.Label>
          </HStack>
          {uploading ? (
            <ActivityIndicator
              size="small"
              style={{ transform: [{ scale: rpx(0.7) }] }}
            />
          ) : error ? (
            <TouchableOpacity onPress={onRetry}>
              <Icon name="error" size={rpx(24)} color={StatusColors.Danger} />
            </TouchableOpacity>
          ) : (
            <LinkButton text="重签" onPress={onResign} />
          )}
        </HStack>
        {signatureImageUrl != null ? (
          <View
            css={`
              height: ${rpx(150)}px;
            `}
          >
            <Img
              uri={signatureImageUrl}
              resizeMode="contain"
              css={`
                flex: 1;
              `}
            />
          </View>
        ) : (
          <Center
            css={`
              padding: ${rpx(44)}px;
              padding-top: ${rpx(30)}px;
              height: ${rpx(150)}px;
            `}
          >
            <TouchableOpacity
              css={`
                border-style: dashed;
                border-width: 1px;
                border-color: #ccc;
                width: ${rpx(210)}px;
                height: ${rpx(68)}px;
                align-items: center;
                justify-content: center;
              `}
              onPress={onPress}
            >
              <Typography.Label light color="#aaa">
                点击签名
              </Typography.Label>
            </TouchableOpacity>
          </Center>
        )}
      </View>
    );
  },
);

const SignIcon = memo(() => {
  const { rpx } = useResponsiveContext();
  return (
    <Svg width={rpx(24)} height={rpx(24)} viewBox="0 0 18 16" fill="none">
      <Path
        d="M17.4 14.8544C17.7314 14.8544 18 15.1108 18 15.4272C18 15.739 17.739 15.9927 17.4142 15.9998L17.4 16H0.6C0.268635 16 0 15.7435 0 15.4272C0 15.1154 0.26101 14.8617 0.585833 14.8546L0.6 14.8544H17.4ZM11.428 0.458272C12.1179 -0.165019 13.2021 -0.150782 13.8737 0.490406L15.5859 2.12497C16.2708 2.77887 16.2708 3.83899 15.5859 4.49288L9.41799 10.3813C8.86702 10.9072 8.19965 11.3087 7.4649 11.5561L3.7006 12.8236C3.34001 12.945 2.94462 12.7644 2.81745 12.4201C2.77439 12.3036 2.76642 12.1778 2.79443 12.0572L3.5651 8.73945C3.79062 7.76865 4.31542 6.88457 5.07187 6.20114L11.428 0.458272Z"
        fill="#207FE7"
      />
    </Svg>
  );
});
